import { useEffect, useState } from 'react';
import { Modal, TextArea, TextInput } from '@carbon/react';
import style from './modals.module.scss';

interface SaveEvaluationModalProps {
  isOpen: boolean;
  isLoading: boolean;
  onClose: () => void;
  onSubmit: (description: string, notes: string) => void;
}

/**
 * Modal shown when the user clicks Save. Collects an optional description
 * and notes before the parent persists the evaluation.
 *
 * @param {SaveEvaluationModalProps} props - Component props.
 * @return {JSX.Element} The save evaluation modal.
 */
export const SaveEvaluationModal = ({
  isOpen,
  isLoading,
  onClose,
  onSubmit,
}: SaveEvaluationModalProps): JSX.Element => {
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (isOpen) {
      setDescription('');
      setNotes('');
    }
  }, [isOpen]);

  /**
   * Handles form submission by delegating the description and notes to the parent.
   */
  const handleSubmit = () => {
    onSubmit(description.trim(), notes.trim());
  };

  return (
    <Modal
      open={isOpen}
      modalHeading="Save"
      primaryButtonText={isLoading ? 'Saving...' : 'Save'}
      primaryButtonDisabled={isLoading}
      secondaryButtonText="Cancel"
      onRequestSubmit={handleSubmit}
      onRequestClose={isLoading ? undefined : onClose}
      className={style.saveModal}
    >
      <p className={style.description}>
        This saves your current work as an Evaluation. Saved evaluations
        can&apos;t be edited. Saving will also clear your current work session
        and start a new work session.
      </p>
      <TextInput
        id="save-evaluation-description"
        labelText="Description (optional)"
        value={description}
        disabled={isLoading}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setDescription(e.target.value)
        }
      />
      <TextArea
        className={style.fieldGap}
        id="save-evaluation-notes"
        labelText="Notes (optional)"
        value={notes}
        disabled={isLoading}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
          setNotes(e.target.value)
        }
        rows={3}
      />
    </Modal>
  );
};
